import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  className?: string;
}

interface TheadProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  className?: string;
}

interface TbodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  className?: string;
}

interface TrProps extends React.HTMLAttributes<HTMLTableRowElement> {
  onClick?: () => void;
  className?: string;
}

interface ThProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sorted?: 'asc' | 'desc' | false;
  onSort?: () => void;
  className?: string;
}

interface TdProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  className?: string;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className = '', ...rest }, ref) => (
    <table
      ref={ref}
      className={`w-full border-collapse ${className}`.trim()}
      {...rest}
    />
  ),
);

Table.displayName = 'Table';

const Thead = React.forwardRef<HTMLTableSectionElement, TheadProps>(
  ({ className = '', ...rest }, ref) => (
    <thead ref={ref} className={className || undefined} {...rest} />
  ),
);

Thead.displayName = 'Thead';

const Tbody = React.forwardRef<HTMLTableSectionElement, TbodyProps>(
  ({ className = '', ...rest }, ref) => (
    <tbody
      ref={ref}
      className={`divide-y divide-subtle ${className}`.trim()}
      {...rest}
    />
  ),
);

Tbody.displayName = 'Tbody';

const Tr = React.forwardRef<HTMLTableRowElement, TrProps>(
  ({ onClick, className = '', ...rest }, ref) => {
    const baseClass =
      'transition-colors hover:bg-surface-primary' +
      (onClick ? ' cursor-pointer' : '');
    return (
      <tr
        ref={ref}
        className={`${baseClass} ${className}`.trim()}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        {...rest}
      />
    );
  },
);

Tr.displayName = 'Tr';

const Th = React.forwardRef<HTMLTableCellElement, ThProps>(
  ({ sorted, onSort, className = '', children, ...rest }, ref) => {
    const baseClass =
      'px-4 py-3 text-left text-2xs font-medium text-content-muted uppercase tracking-wider';
    const isSortable = onSort != null;
    const content = (
      <>
        {children}
        {sorted === 'asc' && (
          <ChevronUp size={12} className="ml-1 inline-block shrink-0" aria-hidden />
        )}
        {sorted === 'desc' && (
          <ChevronDown size={12} className="ml-1 inline-block shrink-0" aria-hidden />
        )}
      </>
    );

    if (isSortable) {
      return (
        <th ref={ref} className={`${baseClass} ${className}`.trim()} {...rest}>
          <button
            type="button"
            onClick={onSort}
            className="inline-flex w-full items-center justify-start gap-1 outline-none focus-visible:ring-2 focus-visible:ring-brand/50 focus-visible:ring-offset-1 rounded"
          >
            {content}
          </button>
        </th>
      );
    }

    return (
      <th ref={ref} className={`${baseClass} ${className}`.trim()} {...rest}>
        {content}
      </th>
    );
  },
);

Th.displayName = 'Th';

const Td = React.forwardRef<HTMLTableCellElement, TdProps>(
  ({ className = '', ...rest }, ref) => (
    <td
      ref={ref}
      className={`px-4 py-3 text-sm text-content-secondary ${className}`.trim()}
      {...rest}
    />
  ),
);

Td.displayName = 'Td';

export { Table, Thead, Tbody, Tr, Th, Td };
export type { TableProps, TheadProps, TbodyProps, TrProps, ThProps, TdProps };
